import { Inject } from '@angular/core';
import { ChangeDetectorRef, Component, Input, effect } from '@angular/core';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { PatientTargetDataModel, PatientTargetModel } from '../../../core/api/models';
import { SESSION_DATA_SOURCE, SessionDataSource } from '../../../core/services/session-data-source';
import { AlertService } from '../../../shared/services/alert.service';

@Component({
	selector: 'app-box-currency',
	templateUrl: './box-currency.component.html',
	standalone: false,
})
export class BoxCurrencyComponent extends BaseBoxComponent {
	@Input() isTopBox: boolean = true;
	@Input() isBottomBox: boolean = false;
	@Input() target: PatientTargetModel | undefined = undefined;
	@Input() isPreview?: boolean;

	text: string | undefined = '-';
	targetID: number = 0;

	currencySymbol: string = '$';
	currentBalance: number = 0;

	targetData: PatientTargetDataModel[] = [];
	transactions: { Delta: number; RecordedDate: string; FormattedDate: string }[] = [];

	// Calculator state
	selectedDelta: number = 0;
	customAmount: number | null = null;
	customSign: 1 | -1 = 1;

	constructor(
		protected override cdRef: ChangeDetectorRef,
		@Inject(SESSION_DATA_SOURCE) private patientSessionService: SessionDataSource,
		private alertService: AlertService
	) {
		super(cdRef);

		effect(() => {
			this.patientSessionService.dataChanged();
			const targetData = this.patientSessionService.getTargetData(this.targetID);
			if (targetData) {
				// exclude deleted
				this.targetData = targetData.filter(t => !t.DeletedDate);
				// order ascending by RecordedDate
				this.targetData.sort((a, b) => (a.RecordedDate ?? '').localeCompare(b.RecordedDate ?? ''));
				// build transactions list
				this.transactions = this.targetData.map(t => ({
					Delta: t.IntValue ?? 0,
					RecordedDate: t.RecordedDate ?? '',
					FormattedDate: this.formatDate(new Date(t.RecordedDate ?? ''))
				}));
			} else {
				this.targetData = [];
				this.transactions = [];
			}
			this.computeCurrentBalance();
		});
	}

	ngOnInit(): void {
		this.text = this.target?.TargetName ?? '-';
		this.targetID = this.target?.ID ?? 0;
		this.currencySymbol = this.target?.CurrencySymbol ?? '$';
		this.computeCurrentBalance();
	}

	override doLongPress(): void {
		this.showExtra(); // open history
	}

	private computeCurrentBalance(): void {
		// CurrentBalance is the authoritative overall value.
		// PatientSessionService keeps it updated locally for offline adds/edits.
		if (this.target?.CurrentBalance != null) {
			this.currentBalance = this.target.CurrentBalance;
			return;
		}

		// Fallback for legacy data where CurrentBalance is not available.
		const allTargetData = this.patientSessionService.getAllTargetDataForTarget(this.targetID);
		const starting = this.target?.StartingBalance ?? 0;
		const totalDelta = (allTargetData || []).reduce((sum, t) => sum + (t.IntValue ?? 0), 0);
		this.currentBalance = starting + totalDelta;
	}

	openCalculator(): void {
		this.selectedDelta = 0;
		this.customAmount = null;
		this.customSign = 1;
		this.showExtra2();
	}

	selectQuick(delta: number): void {
		this.selectedDelta = delta;
		this.customAmount = null;
		this.customSign = 1;
	}

	setCustomSign(sign: 1 | -1): void {
		this.customSign = sign;
	}

	get resultingBalance(): number {
		const custom = (this.customAmount ?? 0) * this.customSign;
		return this.currentBalance + (this.selectedDelta || 0) + custom;
	}

	confirmOperation(): void {
		let delta = this.selectedDelta;
		const custom = (this.customAmount ?? 0) * this.customSign;
		delta += custom;
		if (!delta || isNaN(delta)) {
			return;
		}

		// Check if the resulting balance would be negative and if negative balances are not allowed
		const resultingBalance = this.resultingBalance;
		if (resultingBalance < 0 && this.target?.CanBeNegative === false) {
			this.alertService.error('This target does not allow negative balances. The resulting balance cannot be negative.');
			return;
		}

		// prevent duplicate within same second
		const recordedDate = new Date().toISOString().slice(0, 19).replace('T', ' ');
		const dateToSave = new Date(recordedDate).fromUTCtoServerString();
		const isDuplicate = this.targetData.some(t => t.RecordedDate === dateToSave);
		if (isDuplicate) {
			return;
		}

		this.patientSessionService.addTargetData({
			PatientTargetID: this.targetID,
			IntValue: delta,
			TextValue: (delta > 0 ? '+' : '') + delta.toString(),
			RecordedDate: dateToSave
		});

		this.closeExtra2();
	}

	formatCurrency(value: number): string {
		// integer currency for now
		return `${this.currencySymbol}${(value ?? 0).toFixed(0)}`;
	}

	balanceAtIndex(index: number): number {
		// For history display, compute from balance before this session + current session cumulative deltas.
		const sessionTotal = this.transactions.reduce((acc, cur) => acc + (cur.Delta || 0), 0);
		const starting = this.currentBalance - sessionTotal;
		const sum = this.transactions.slice(0, index + 1).reduce((acc, cur) => acc + (cur.Delta || 0), 0);
		return starting + sum;
	}
}


